import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Users, Code, Shield, AlertTriangle, Mail, FileText, Clock, Award } from "lucide-react";

export default function AIHackathonTermsOfService() {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-secondary mb-4">AI Hackathon Rules, Terms, and Conditions</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Please read these terms carefully before participating in our AI hackathon.
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-8">
          <CardContent className="py-6">
            <p className="text-gray-700 leading-relaxed">
              <strong>Udyogapramoda</strong> is a voluntary service dedicated to fostering innovation and learning through hackathons.
              By participating in this hackathon, you agree to abide by the following rules, terms, and conditions.
              These guidelines ensure a fair, ethical, and collaborative environment while protecting all parties involved.
            </p>
          </CardContent>
        </Card>

        {/* Rules Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl font-bold text-secondary">
              <FileText className="w-6 h-6 mr-3 text-primary" />
              Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Rule 1: Eligibility */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Users className="w-5 h-5 mr-2 text-primary" />
                1. Eligibility and Participation
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Age is not a barrier; participants of any age are welcome, provided they are at least 18 years old for legal consent purposes.</li>
                <li>• Teams must consist of a minimum of 2 and a maximum of 5 members. Solo participation is not allowed.</li>
                <li>• All participants must register in advance via the provided form.</li>
                <li>• If rules are violated, the Udyogapramoda team reserves the right to disqualify any member or team at any time.</li>
              </ul>
            </div>

            {/* Rule 2: Code Originality */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Code className="w-5 h-5 mr-2 text-primary" />
                2. Code Originality and Ownership
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• All submitted code, prototypes, and artifacts must be original work created during the hackathon.</li>
                <li>• Participants are free to use any AI tools (e.g., generative AI like GPT models, code assistants like GitHub Copilot) to aid in development, but they must own the final code and outputs.</li>
                <li>• This means participants are responsible for reviewing, understanding, and claiming authorship of all code, ensuring it aligns with their original contributions.</li>
                <li>• Pre-existing code or templates are allowed only if they are open-source and properly attributed, but the core innovation must be developed during the event.</li>
                <li>• No copying or plagiarizing from external sources without proper modification and attribution.</li>
              </ul>
            </div>

            {/* Rule 3: Submissions */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <FileText className="w-5 h-5 mr-2 text-primary" />
                3. Submissions
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Submissions must include source code (via GitHub or similar), a demo video (2-5 minutes), and a brief README explaining the project.</li>
                <li>• All submissions must be uploaded by the deadline; late entries will not be considered.</li>
                <li>• Projects must align with the hackathon themes (e.g., generative AI applications for students, job seekers, financial planning, spiritual health, etc.).</li>
              </ul>
            </div>

            {/* Rule 4: Event Format */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Clock className="w-5 h-5 mr-2 text-primary" />
                4. Event Format and Duration
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• The hackathon will be held in person in Hyderabad.</li>
                <li>• Duration is 36 hours, during which teams must develop, demonstrate their solution, and present their code.</li>
              </ul>
            </div>

            {/* Rule 5: Conduct */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Users className="w-5 h-5 mr-2 text-primary" />
                5. Conduct
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Participants must respect others, avoid harassment, and maintain a positive environment.</li>
                <li>• Use of hackathon resources (e.g., APIs, mentors) is encouraged but must be fair.</li>
                <li>• Decisions by judges and organizers are final.</li>
              </ul>
            </div>

            {/* Rule 6: Judging */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Award className="w-5 h-5 mr-2 text-primary" />
                6. Judging
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Projects will be evaluated based on innovation, technical execution, impact, user experience, and adherence to themes (as outlined in problem statements).</li>
                <li>• Bonus points for ethical AI use, cultural sensitivity (e.g., in Madhwa-inspired features), and scalability.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Terms and Conditions Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl font-bold text-secondary">
              <Shield className="w-6 h-6 mr-3 text-primary" />
              Terms and Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Term 1: Intellectual Property */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <FileText className="w-5 h-5 mr-2 text-primary" />
                1. Intellectual Property
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Participants retain full ownership of their submissions and any intellectual property created during the hackathon.</li>
                <li>• By submitting, participants grant Udyogapramoda a non-exclusive, royalty-free license to showcase, promote, or reference the project for promotional purposes (e.g., on websites or social media).</li>
                <li>• Participants warrant that their submissions do not infringe on any third-party copyrights, trademarks, or other intellectual property rights.</li>
              </ul>
            </div>

            {/* Term 2: Use of AI Tools */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Code className="w-5 h-5 mr-2 text-primary" />
                2. Use of AI Tools
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• AI tools are permitted for ideation, code generation, debugging, and other aspects of development.</li>
                <li>• However, participants must ensure they are the rightful owners of the final code by actively contributing, modifying, and verifying AI-generated content.</li>
                <li>• Blind copying from AI without understanding or ownership is prohibited.</li>
              </ul>
            </div>

            {/* Term 3: Liability */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                3. Liability and Indemnification
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Udyogapramoda, as a voluntary service, assumes no liability for any legal actions, including suits for copyright infringement, arising from participants' submissions.</li>
                <li>• All liability for infringement, misuse of intellectual property, or any disputes lies solely with the individual or team who submitted the artifact.</li>
                <li>• Participants agree to indemnify and hold harmless Udyogapramoda, its organizers, volunteers, and affiliates from any claims, damages, or expenses related to their participation or submissions.</li>
              </ul>
            </div>

            {/* Term 4: Data Privacy */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <Shield className="w-5 h-5 mr-2 text-primary" />
                4. Data Privacy
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Personal information collected during registration will be used solely for hackathon purposes and not shared with third parties without consent.</li>
                <li>• Participants are responsible for handling any user data in their projects ethically and in compliance with applicable laws (e.g., GDPR or India's DPDP Act).</li>
              </ul>
            </div>

            {/* Term 5: Disclaimers */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                5. Disclaimers
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• Udyogapramoda provides this hackathon as a voluntary, non-profit service. There are no guarantees of prizes, outcomes, or opportunities.</li>
                <li>• Organizers reserve the right to disqualify participants for violations of rules or unethical behavior.</li>
                <li>• In case of technical issues, disputes, or unforeseen events, organizers may adjust rules or schedules as needed.</li>
              </ul>
            </div>

            {/* Term 6: Acceptance */}
            <div>
              <h3 className="flex items-center text-lg font-semibold text-secondary mb-3">
                <FileText className="w-5 h-5 mr-2 text-primary" />
                6. Acceptance
              </h3>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li>• By registering and participating, you acknowledge that you have read, understood, 
                and agree to be bound by these Rules, Terms, and Conditions.</li>
                <li>• These terms are governed by the laws of India.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="mt-12">
          <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
            <CardContent className="py-8 text-center">
              <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-secondary mb-2">
                Questions About Hackathon Terms?
              </h3>
              <p className="text-gray-600 mb-4">
                For questions about these hackathon rules, terms, and conditions, contact the organizers.
              </p>
                             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800">
                 <strong>Contact:</strong> <a href="mailto:info@udyogapramoda.org" className="underline">info@udyogapramoda.org</a>
                 <br />
                 We encourage creativity, collaboration, and ethical innovation—happy hacking!
               </div>
            </CardContent>
          </Card>
        </div>

        {/* Acceptance Notice */}
        <div className="mt-8">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-6 text-center">
              <p className="text-green-800">
                <strong>By participating in this hackathon, you acknowledge that you have read, understood, 
                and agree to be bound by these Rules, Terms, and Conditions.</strong>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 